import * as React from 'react';
import dateformat from 'dateformat';
import './PlaceListPage.css';
import { Link, useHistory } from 'react-router-dom';
import { Loading } from '../Loading';
import {
  useGetPlacesQuery,
  useUpdatePlacesCompletedMutation,
} from '../generated/graphql';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { AuthButton, Button } from '../components/Button';
import { PlaceFilterDropdown } from '../components/PlaceFilterDropdown';
import { PlaceFilterOperatorLabels, PlaceFilterPopover } from '../components/PlaceFilterPopover';
import { Checkbox } from '../components/Input';
import { PlaceListBulkBar } from '../components/PlaceListBulkBar';
import { PlaceBulkDeleteDialog } from '../components/PlaceBulkDeleteDialog';
import { ToastContext } from '../components/Toast';
import { AppContext } from '../AppContext';
import { usePlaceBulkDelete } from './PlaceListPage.bulkDelete';
import {
  PlaceListView,
  togglePlaceSelection,
  areAllDisplayedSelected,
  toggleAllDisplayedSelection,
  isBulkBarVisible,
  toCompletedQueryVariable,
  toCompletedValueToWrite,
  getBulkActionLabel,
  getBulkActionSuccessMessage,
  groupTagsByRow,
  toPlaceTagKey,
  findPlaceIdsByKind,
  matchesTagConditions,
  applyFilterInversion,
  getEmptyPlacesMessage,
  NegatedFilters,
  PlaceAttributeFilter,
  PlaceAttributeGroup,
  EMPTY_ATTRIBUTE_FILTER,
  describeAttributeConditions,
  clearAttributeGroup,
  matchesAttributeFilter,
  PlaceSort,
  PlaceSortKey,
  PLACE_SORT_OPTIONS,
  getSortDirectionMark,
  describePlaceSort,
  choosePlaceSortKey,
  sortPlaces
} from './PlaceListPage.logic';
import { TableScroll } from '../components/TableScroll';
import { EmptyRow } from '../components/EmptyRow';


// 仕切り線の右に常に並べるフィルタ。タグを先頭に置き、残りは表の列と同じ順に並べる
type PlaceFilterKind = 'name' | 'USER' | 'CHIEF' | 'CUSTOMER' | 'created' | 'updated';
const FILTER_OPTIONS: { kind: PlaceFilterKind; label: string }[] = [
  { kind: 'USER', label: 'タグ' },
  { kind: 'name', label: '物件名' },
  { kind: 'CHIEF', label: '責任者' },
  { kind: 'CUSTOMER', label: 'お客様' },
  { kind: 'created', label: '作成日' },
  { kind: 'updated', label: '更新日' }
];

// 「条件に一致する／一致しない」の言葉を、フィルタの入力の形（文字・選択肢・範囲）に合わせて選ぶ
// ボタンの条件の後ろの言葉は、条件の要約（「見本」、「防水、塗装」、「2026/01/01〜」など）に続けて自然に読めるものにする
const TEXT_OPERATOR_LABELS: PlaceFilterOperatorLabels = {
  match: 'を含む',
  notMatch: 'を含まない',
  matchSuffix: 'を含む',
  negatedSuffix: 'を含まない'
};
const CHOICE_OPERATOR_LABELS: PlaceFilterOperatorLabels = {
  match: 'のいずれかに当てはまる',
  notMatch: 'のどれにも当てはまらない',
  matchSuffix: '',
  negatedSuffix: '以外'
};
const RANGE_OPERATOR_LABELS: PlaceFilterOperatorLabels = {
  match: 'が範囲内',
  notMatch: 'が範囲外',
  matchSuffix: '',
  negatedSuffix: 'を除く'
};
function getOperatorLabels(kind: PlaceFilterKind): PlaceFilterOperatorLabels {
  if (kind === 'name') {
    return TEXT_OPERATOR_LABELS;
  }
  if (kind === 'created' || kind === 'updated') {
    return RANGE_OPERATOR_LABELS;
  }
  return CHOICE_OPERATOR_LABELS;
}

// 並び替えのメニューの昇順・降順のボタンのクラス名。選んでいる向きのボタンを強調する
function getSortDirectionButtonClassName(selected: PlaceSort['direction'], direction: PlaceSort['direction']): string {
  if (selected === direction) {
    return 'active';
  }
  return '';
}

const SORT_HEADING_ID = 'PlaceListPage-sort_heading';
const FILTER_HEADING_ID = 'PlaceListPage-filter_heading';

export const PlaceListPage: React.FC = function () {
  // inProgress: 物件一覧 / completed: アーカイブ済みの物件一覧。ページは移らず、同じ画面で切り替える
  const [view, setView] = React.useState<PlaceListView>('inProgress');
  const [selectedPlaceIds, setSelectedPlaceIds] = React.useState<number[]>([]);
  const { loading, data, error, refetch } = useGetPlacesQuery({
    variables: { completed: toCompletedQueryVariable(view) },
  });
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const appContext = React.useContext(AppContext);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  // 「条件に一致しない」にしたフィルタ
  const [negatedFilters, setNegatedFilters] = React.useState<NegatedFilters>({});
  const [attributeFilter, setAttributeFilter] = React.useState<PlaceAttributeFilter>(EMPTY_ATTRIBUTE_FILTER);
  // 「選択した条件以外の物件を表示」。すべての絞り込みの条件をまとめた結果を入れ替える
  const [invertFilters, setInvertFilters] = React.useState(false);
  const [sort, setSort] = React.useState<PlaceSort | undefined>(undefined);
  // 並び替えのメニューの先頭で選んでいる向き。項目を選ぶ前に選んだ向きも覚えておく
  const [sortDirection, setSortDirection] = React.useState<PlaceSort['direction']>('asc');

  const [
    updatePlacesCompleted,
    updatePlacesCompletedResult,
  ] = useUpdatePlacesCompletedMutation({
    onCompleted() {
      toastContext.showToast(
        getBulkActionSuccessMessage(view),
        'success',
        3000
      );
      setSelectedPlaceIds([]);
      refetch();
    },
    onError() {
      toastContext.showToast('更新に失敗しました', 'error', 5000);
    },
  });

  // まとめての削除が終わったら一覧を取り直し、削除できなかった物件と実行しなかった物件だけを選択に残して、やり直せるようにする
  const bulkDelete = usePlaceBulkDelete((result) => {
    const remainingPlaceIds = result.failedTargets
      .concat(result.skippedTargets)
      .map((target) => target.id);

    // 失敗や中止があったときは結果のダイアログで知らせるので、すべて削除できたときだけ成功の通知を出す
    if (remainingPlaceIds.length === 0) {
      toastContext.showToast(
        `${result.deletedCount}件の物件を削除しました`,
        'success',
        3000
      );
    }

    setSelectedPlaceIds(remainingPlaceIds);
    refetch();
  });

  // アーカイブの付け外しか、まとめての削除を実行している間は、選択や一覧の切り替えを止めて、実行中の対象が変わらないようにする
  const isBulkActionRunning =
    updatePlacesCompletedResult.loading || bulkDelete.state.kind !== 'idle';

  // アーカイブの付け外しとまとめての削除ができるのは、新規作成と同じ権限を持つ人だけ。
  // 削除はサーバーが権限の段階2以上でしか受け付けないので、選択の列と選択中のバーはその人にだけ出す
  const me = appContext.state.me;
  const canEdit = me !== undefined && me.authority.level >= 2;
  const bulkBarVisible = canEdit && isBulkBarVisible(selectedPlaceIds.length);
  // 表の列数（選択の列は、アーカイブの付け外しができる人にだけ出す）。0件の行を表の幅いっぱいに広げるために使う
  let placeTableColumnCount = 6;
  if (canEdit) {
    placeTableColumnCount = 7;
  }

  if (error) {
    return <GraphQLErrorDialog error={error} />;
  }

  const placeTags = data?.getPlaceTags ?? [];

  // 絞り込みと並び替えを適用した後、実際に表として表示する物件。
  // タグ・責任者・お客様は同じ種類の中で選んだものを OR、種類どうしと物件名・面数・日付の条件は AND でつなぐ。
  // 「条件に一致しない」にしたフィルタは、当てはまらない物件を残す
  const placeIdsByKind = findPlaceIdsByKind(placeTags, selectedTags);
  const attributeConditions = describeAttributeConditions(attributeFilter);
  const hasConditions = selectedTags.length > 0 || attributeConditions.length > 0;
  let displayedPlaces: NonNullable<typeof data>['getPlaces'] = data?.getPlaces ?? [];
  displayedPlaces = displayedPlaces.filter(place => {
    const matched = matchesTagConditions(place.id, placeIdsByKind, negatedFilters) && matchesAttributeFilter(place, attributeFilter, negatedFilters);
    return applyFilterInversion(matched, invertFilters, hasConditions);
  });
  displayedPlaces = sortPlaces(displayedPlaces, sort);
  const displayedPlaceIds = displayedPlaces.map(place => place.id);

  // 表示される物件が変わるので、見えなくなった物件が更新対象に残らないよう選択を空にする
  // 選択は「行＋名前」のキーで持つ。同じ名前でも種類が違えば別々に選ぶ
  const changeConditions = (nextSelectedTags: string[], nextAttributeFilter: PlaceAttributeFilter) => {
    setSelectedPlaceIds([]);
    setSelectedTags(nextSelectedTags);
    setAttributeFilter(nextAttributeFilter);
    // 条件がすべて外れたら「選択した条件以外の物件を表示」も外す。次に条件を入れたときに、気づかないうちに入れ替わった結果が出ないようにする
    if (nextSelectedTags.length === 0 && describeAttributeConditions(nextAttributeFilter).length === 0) {
      setInvertFilters(false);
    }
  };
  const changeInvertFilters = (inverted: boolean) => {
    setSelectedPlaceIds([]);
    setInvertFilters(inverted);
  };
  let invertLabelClassName = 'PlaceListPage-invert';
  if (!hasConditions) {
    invertLabelClassName += ' disabled';
  }
  const changeNegated = (kind: PlaceFilterKind, negated: boolean) => {
    setSelectedPlaceIds([]);
    setNegatedFilters({ ...negatedFilters, [kind]: negated });
  };
  const toggleTag = (tagKey: string) => {
    if (selectedTags.indexOf(tagKey) !== -1) {
      changeConditions(selectedTags.filter(t => t !== tagKey), attributeFilter);
    } else {
      changeConditions(selectedTags.concat([tagKey]), attributeFilter);
    }
  };
  const setTagsSelected = (tags: { kind: string; name: string }[], selected: boolean) => {
    const tagKeys = tags.map(toPlaceTagKey);
    const others = selectedTags.filter(t => tagKeys.indexOf(t) === -1);
    changeConditions(selected ? others.concat(tagKeys) : others, attributeFilter);
  };
  const updateAttributeFilter = (patch: Partial<PlaceAttributeFilter>) => {
    changeConditions(selectedTags, { ...attributeFilter, ...patch });
  };
  const clearAttribute = (group: PlaceAttributeGroup) => {
    changeConditions(selectedTags, clearAttributeGroup(attributeFilter, group));
  };
  // × でそのフィルタの条件をすべて外し、「一致しない」にしていた場合も「一致する」に戻す。
  // タグ・責任者・お客様は、その種類で選んだものをすべて外す
  const removeFilter = (kind: PlaceFilterKind) => {
    setNegatedFilters({ ...negatedFilters, [kind]: false });
    if (kind === 'USER' || kind === 'CHIEF' || kind === 'CUSTOMER') {
      changeConditions(selectedTags.filter(tagKey => tagKey.indexOf(`${kind}:`) !== 0), attributeFilter);
    } else {
      clearAttribute(kind);
    }
  };
  const changeView = (nextView: PlaceListView) => {
    setSelectedPlaceIds([]);
    setView(nextView);
  };
  const { userTags, chiefTags, customerTags } = groupTagsByRow(placeTags);
  const chosenTags = placeTags.filter(tag => selectedTags.indexOf(toPlaceTagKey(tag)) !== -1);
  // フィルタのボタンに共通で渡す値。枠内の × は、そのフィルタに条件が設定されているときだけ出す
  const filterPopoverProps = (kind: PlaceFilterKind) => {
    let onRemove: (() => void) | undefined;
    if (isFilterActive(kind)) {
      onRemove = () => removeFilter(kind);
    }
    return {
      key: kind,
      negated: !!negatedFilters[kind],
      onNegatedChange: (negated: boolean) => changeNegated(kind, negated),
      operatorLabels: getOperatorLabels(kind),
      onRemove
    };
  };
  const renderDropdown = (kind: PlaceFilterKind, label: string, tags: typeof placeTags) => (
    <PlaceFilterDropdown
      {...filterPopoverProps(kind)}
      label={label}
      items={tags}
      getKey={toPlaceTagKey}
      isSelected={tag => selectedTags.indexOf(toPlaceTagKey(tag)) !== -1}
      onToggle={tag => toggleTag(toPlaceTagKey(tag))}
      onSetSelected={setTagsSelected}
    />
  );

  const isAttributeActive = (group: PlaceAttributeGroup) => attributeConditions.some(condition => condition.group === group);
  const describeAttributeGroup = (group: PlaceAttributeGroup) => attributeConditions.find(condition => condition.group === group)?.text;
  // フィルタに条件が設定されているか。設定されているフィルタだけ、ボタンの枠内に × を出す
  const isFilterActive = (kind: PlaceFilterKind) => {
    if (kind === 'USER' || kind === 'CHIEF' || kind === 'CUSTOMER') {
      return chosenTags.some(tag => tag.kind === kind);
    }
    return isAttributeActive(kind);
  };
  const renderNameFilter = () => (
    <PlaceFilterPopover {...filterPopoverProps('name')} label="物件名" activeCount={0} active={isAttributeActive('name')} summary={describeAttributeGroup('name')} menuClassName="input_menu">
      {() => (
        <input
          className="PlaceFilterDropdown-search"
          type="text"
          placeholder="物件名に含む文字"
          value={attributeFilter.name}
          autoFocus
          onChange={event => updateAttributeFilter({ name: event.target.value })}
        />
      )}
    </PlaceFilterPopover>
  );
  // 作成日・更新日の範囲。片方だけの入力なら「以降」「以前」になる。
  // 条件を外すときは、ボタンの × か、日付の欄を空にする（物件名の入力欄を空にするのと同じ外し方）
  const renderDateRangeFilter = (
    label: string,
    group: 'created' | 'updated',
    fromKey: keyof PlaceAttributeFilter,
    toKey: keyof PlaceAttributeFilter
  ) => (
    <PlaceFilterPopover {...filterPopoverProps(group)} label={label} activeCount={0} active={isAttributeActive(group)} summary={describeAttributeGroup(group)} menuClassName="input_menu">
      {() => (
        <div className="PlaceFilterDropdown-range">
          <input
            type="date"
            aria-label={`${label}（から）`}
            value={attributeFilter[fromKey]}
            onChange={event => updateAttributeFilter({ [fromKey]: event.target.value })}
          />
          〜
          <input
            type="date"
            aria-label={`${label}（まで）`}
            value={attributeFilter[toKey]}
            onChange={event => updateAttributeFilter({ [toKey]: event.target.value })}
          />
        </div>
      )}
    </PlaceFilterPopover>
  );
  const renderFilter = (kind: PlaceFilterKind) => {
    if (kind === 'name') {
      return renderNameFilter();
    }
    if (kind === 'created') {
      return renderDateRangeFilter('作成日', 'created', 'createdFrom', 'createdTo');
    }
    if (kind === 'updated') {
      return renderDateRangeFilter('更新日', 'updated', 'updatedFrom', 'updatedTo');
    }
    if (kind === 'USER') {
      return renderDropdown(kind, 'タグ', userTags);
    }
    if (kind === 'CHIEF') {
      return renderDropdown(kind, '責任者', chiefTags);
    }
    return renderDropdown(kind, 'お客様', customerTags);
  };
  // 並び替えも絞り込みと同じボタン＋メニューにする。向き（昇順・降順）をメニューの先頭で切り替え、その下で項目を選ぶ。
  // 絞り込みのボタンと見分けられるよう、文字の左に上下の矢印の印を付ける。
  // 絞り込みのボタンと同じく、並び替えを設定しているときだけ枠内に × を出し、押すと並び替えを外す
  // 並び替えを外したら、覚えている向きも初めの昇順に戻す
  let removeSort: (() => void) | undefined;
  if (sort !== undefined) {
    removeSort = () => {
      setSort(undefined);
      setSortDirection('asc');
    };
  }
  // 向きを選んだら覚えておき、並び替え中ならその向きで並べ直す
  const changeSortDirection = (direction: PlaceSort['direction']) => {
    setSortDirection(direction);
    if (sort !== undefined) {
      setSort({ key: sort.key, direction });
    }
  };
  const renderSortMenu = () => (
    <PlaceFilterPopover label={describePlaceSort(sort)} activeCount={0} active={sort !== undefined} menuClassName="input_menu" onRemove={removeSort} removeLabel="並び替えを外す">
      {() => (
        <>
          {/* 向きは項目を選ぶ前でも選べる。選んだ向きを覚えておき、項目を選んだときにその向きで並べる */}
          <div className="PlaceFilterDropdown-segment PlaceListPage-sort_direction">
            {(['asc', 'desc'] as const).map(direction => (
              <button
                key={direction}
                type="button"
                className={getSortDirectionButtonClassName(sortDirection, direction)}
                onClick={() => changeSortDirection(direction)}
              >
                {direction === 'asc' ? '昇順' : '降順'} {getSortDirectionMark(direction)}
              </button>
            ))}
          </div>
          <div className="PlaceFilterDropdown-list">
            {PLACE_SORT_OPTIONS.map(option => (
              <label key={option.key} className="PlaceFilterDropdown-item">
                <input
                  type="radio"
                  name="PlaceListPage-sort"
                  checked={sort !== undefined && sort.key === option.key}
                  onChange={() => setSort(choosePlaceSortKey(sortDirection, option.key))}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </PlaceFilterPopover>
  );
  // 見出しの右のボタン。物件一覧では「新規作成」と、アーカイブ済みの一覧へ切り替える「アーカイブ一覧」を出す。
  // アーカイブ済みの一覧では物件を作らないので「新規作成」は出さず、物件一覧へ戻る「物件一覧」だけを出す。
  // 一覧を切り替えるボタンは、どちらの一覧でも同じ白のボタンにする
  const renderHeaderButtons = () => {
    if (view === 'completed') {
      return (
        <Button color="white" mini disabled={isBulkActionRunning} onClick={() => changeView('inProgress')}>
          物件一覧
        </Button>
      );
    }
    return (
      <>
        <AuthButton authLv={2} mini onClick={() => history.push('/places/new')}>
          新規作成
        </AuthButton>
        <Button color="white" mini disabled={isBulkActionRunning} onClick={() => changeView('completed')}>
          アーカイブ一覧
        </Button>
      </>
    );
  };
  // 並び替え中の列の見出しに向きの矢印を出す
  const renderHeader = (key: PlaceSortKey, label: string) => {
    const active = sort !== undefined && sort.key === key;
    return (
      <th aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
        {label}
        {active && <span className="PlaceListPage-sort_icon">{getSortDirectionMark(sort!.direction)}</span>}
      </th>
    );
  };

  return (
    <div className="PlaceListPage-container">
      <Loading open={loading || !data} />
      <div className="PlaceListPage-content">
      <div className="PlaceListPage-header">
        <h1>{view === 'completed' ? 'アーカイブ済みの物件一覧' : '物件一覧'}</h1>
        <div className="PlaceListPage-header_buttons">{renderHeaderButtons()}</div>
      </div>
      {bulkBarVisible && (
        <PlaceListBulkBar
          selectedCount={selectedPlaceIds.length}
          actionLabel={getBulkActionLabel(view)}
          disabled={isBulkActionRunning}
          onAction={() => updatePlacesCompleted({ variables: { place_ids: selectedPlaceIds, completed: toCompletedValueToWrite(view) } })}
          onDelete={() => {
            const targets = (data?.getPlaces ?? []).filter((place) =>
              selectedPlaceIds.includes(place.id)
            );

            // 選択していた物件がすでに一覧から消えていたら、削除するものがないので選択だけを空にする
            if (targets.length === 0) {
              setSelectedPlaceIds([]);
            } else {
              bulkDelete.openConfirm(targets);
            }
          }}
        />
      )}
      {/* 選択中はこの行に選択中バーが重なるので、端がはみ出して見えないよう行ごと隠す */}
      <div className={'PlaceListPage-toolbar' + (bulkBarVisible ? ' covered' : '')}>
        {/* 並び替えと絞り込みは、どちらも押せない見出しの後ろにボタンを並べる同じ形にし、ボタンが何の操作かを見出しで示す */}
        <div className="PlaceListPage-toolbar_group" role="group" aria-labelledby={SORT_HEADING_ID}>
          <span className="PlaceListPage-toolbar_heading" id={SORT_HEADING_ID}>
            並び替え
          </span>
          {renderSortMenu()}
        </div>
        <span className="PlaceListPage-toolbar_divider" />
        <div className="PlaceListPage-toolbar_group" role="group" aria-labelledby={FILTER_HEADING_ID}>
          <span className="PlaceListPage-toolbar_heading" id={FILTER_HEADING_ID}>
            絞り込み
          </span>
          {FILTER_OPTIONS.map(option => renderFilter(option.kind))}
          {/* standardの「選択したタグ以外の物件を表示」に当たる切り替え。条件がないときは入れ替える結果がないので押せなくする */}
          <label className={invertLabelClassName}>
            <Checkbox value={invertFilters && hasConditions} disabled={!hasConditions} onChange={changeInvertFilters} />
            選択した条件以外の物件を表示
          </label>
        </div>
      </div>
      <div className="PlaceListPage-result_count">{displayedPlaces.length} 件</div>
      <TableScroll storageKey="places" className="PlaceListPage-table_scroll">
      <table className="App-table PlaceListPage-table">
        {/* 列の幅は中身によらず固定する。絞り込みや表示の切り替えで表の幅・列の幅が変わらないようにする */}
        <colgroup>
          {canEdit && <col className="PlaceListPage-col_check" />}
          <col />
          <col className="PlaceListPage-col_faces" />
          <col className="PlaceListPage-col_person" />
          <col className="PlaceListPage-col_person" />
          <col className="PlaceListPage-col_date" />
          <col className="PlaceListPage-col_date" />
        </colgroup>
        <thead>
          <tr>
            {canEdit && (
              <th className="PlaceListPage-check_cell" data-column-resize="off">
                <Checkbox
                  value={areAllDisplayedSelected(
                    selectedPlaceIds,
                    displayedPlaceIds
                  )}
                  onChange={() => {
                    if (isBulkActionRunning) {
                      return;
                    }
                    setSelectedPlaceIds(
                      toggleAllDisplayedSelection(
                        selectedPlaceIds,
                        displayedPlaceIds
                      )
                    );
                  }}
                />
              </th>
            )}
            {renderHeader('name', '物件名')}
            {renderHeader('faces', '面数')}
            {renderHeader('chief_name', '責任者')}
            {renderHeader('client_name', 'お客様')}
            {renderHeader('created', '作成日')}
            {renderHeader('updated', '更新日')}
          </tr>
        </thead>
        <tbody>
          {displayedPlaces.length === 0 && (
            <EmptyRow colSpan={placeTableColumnCount} message={getEmptyPlacesMessage(view, (data?.getPlaces ?? []).length)} />
          )}
          {displayedPlaces.map((place) => {
            if (!place) {
              return new Error();
            }
            return (
              <tr key={place.id}>
                {canEdit && (
                  <td className="PlaceListPage-check_cell">
                    <Checkbox
                      value={selectedPlaceIds.indexOf(place.id) !== -1}
                      onChange={() => {
                        if (isBulkActionRunning) {
                          return;
                        }
                        setSelectedPlaceIds(
                          togglePlaceSelection(selectedPlaceIds, place.id)
                        );
                      }}
                    />
                  </td>
                )}
                <td>
                  <Link to={`/places/${place.id}`}>{place.name}</Link>
                </td>
                <td>{place.faces.length}</td>
                <td>{place.chief_name}</td>
                <td>{place.client_name}</td>
                <td>{dateformat(place.created, 'yyyy/mm/dd')}</td>
                <td>{dateformat(place.updated, 'yyyy/mm/dd')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </TableScroll>
      </div>
      <PlaceBulkDeleteDialog
        state={bulkDelete.state}
        onConfirm={bulkDelete.startDeletion}
        onCancelConfirm={bulkDelete.cancelConfirm}
        onRequestCancel={bulkDelete.requestCancel}
        onCloseResult={bulkDelete.closeResult}
      />
    </div>
  );
};
